/**
 * 대시보드 배치 서버 동기화 — **판정과 표식** (2026-09-16 · 대시보드 v2 V10 · web-plan §2).
 *
 * ★★★ **이 파일은 네트워크를 모른다.** 「무엇을 해야 하는가」만 정한다(순수 함수).
 *     실제 조회 · 올리기는 `api/dashboard-layout.ts`, 상태 반영은 store 가 한다.
 *     그래야 네 갈래 hydrate · 409 · 오프라인을 **서버 없이** 검사로 고정할 수 있다.
 *
 * ## 지켜야 할 것 (web-plan §2.1)
 *
 * | # | 규칙 | 여기서 어떻게 |
 * |---|---|---|
 * | M1 | 로컬 키의 `v` 는 계속 **1** | 이 파일은 로컬 본문을 **건드리지 않는다**. 표식은 **다른 키**에 쓴다 |
 * | M2 | 로컬 키를 지우지 않는다 | 지우는 코드가 없다 — 서버로 옮긴 뒤에도 거울로 남는다 |
 * | M3 | 모르는 위젯 key · 모르는 설정 칸을 떨어뜨리지 않는다 | 보드를 **그대로** 실어 나른다(칸을 골라 담지 않는다) |
 * | M4 | 회사 판(`kind:'corp'`)은 올리지 않는다 | `stripCorp()` — **`kind` 로 거른다**(id 는 기존 보드 id 를 물려받을 수 있어 못 믿는다) |
 * | M5 | `anon` 키는 이관하지 않는다 | `canSync()` 가 빈 userId 를 막는다 |
 * | M6 | 이관 전후 화면이 같아야 한다 | 판정이 «서버로 교체»일 때만 화면이 바뀐다 — 나머지는 로컬 그대로 |
 */
import type { DashboardBoard } from './dashboardStore';
import type { DashboardLayoutDoc, LayoutFetch } from '../api/dashboard-layout';

/** 서버 문서 판 번호 — 로컬 `v`(1 · M1)와 따로 센다. */
export const LAYOUT_SCHEMA_VERSION = 1;

const STORAGE_PREFIX = 'axi.dashboard.v1';

/** 어디까지 서버와 맞췄는지 — **로컬 본문과 다른 키**에 둔다(본문 모양을 건드리면 옛 판이 배치를 버린다). */
export function syncMarkerKey(userId: string): string {
  return `${STORAGE_PREFIX}.${userId}.sync`;
}

/** 서버 것으로 갈아끼우기 전의 로컬 본문을 **버리지 않고** 옮겨 두는 자리(Q-2). */
export function beforeSyncKey(userId: string): string {
  return `${STORAGE_PREFIX}.${userId}.before-sync`;
}

export interface SyncMarker {
  /** 마지막으로 맞춘 서버 판. */
  revision: number;
  /** 마지막으로 맞춘 시각(ms). */
  at: number;
  /** 올리지 못한 변경이 남아 있다 — 다음 기회에 다시 올린다. */
  dirty?: boolean;
}

export function readMarker(userId: string): SyncMarker | null {
  try {
    const raw = localStorage.getItem(syncMarkerKey(userId));
    if (!raw) return null;
    const m = JSON.parse(raw) as SyncMarker;
    return typeof m?.revision === 'number' ? m : null;
  } catch {
    return null;
  }
}

export function writeMarker(userId: string, marker: SyncMarker): void {
  try {
    localStorage.setItem(syncMarkerKey(userId), JSON.stringify(marker));
  } catch {
    /* 표식을 못 써도 배치는 멀쩡하다 — 다음에 다시 맞춘다. */
  }
}

export function clearMarker(userId: string): void {
  try {
    localStorage.removeItem(syncMarkerKey(userId));
  } catch {
    /* no-op */
  }
}

/**
 * 서버 것으로 갈아끼우기 전에 로컬 본문을 **복사해 둔다**(Q-2 · 「이 브라우저 배치로 바꾸기」의 밑천).
 * ★ 원본은 **그대로 둔다** — 옮기는 것이 아니라 베끼는 것이다(M2).
 */
export function backupLocalBody(userId: string): void {
  try {
    const body = localStorage.getItem(`${STORAGE_PREFIX}.${userId}`);
    if (body) localStorage.setItem(beforeSyncKey(userId), body);
  } catch {
    /* no-op */
  }
}

/**
 * 회사 판을 뺀 보드들 (M4).
 *
 * ★★★ **`kind` 로 거른다.** 회사 판의 id 는 `'corp'` 가 아닐 수 있다 — `applyTemplates` 는
 *     이미 있던 보드 자리에 회사 판을 앉힐 때 **그 보드의 id 를 물려준다**
 *     (`dashboardStore.ts` 의 `id: idx >= 0 ? boards[idx]!.id : 'corp'`).
 *     id 로 걸렀다면 회사 판이 서버로 올라갔을 것이다.
 */
export function stripCorp(boards: DashboardBoard[]): DashboardBoard[] {
  return boards.filter((b) => b.kind !== 'corp');
}

export interface LocalSnapshot {
  activeBoardId: string;
  boards: DashboardBoard[];
  seenForced?: Record<string, number>;
  hiddenBoardIds?: string[];
  defaultBoardId?: string;
}

/** 로컬 상태 → 서버 문서. 보드 속은 **손대지 않는다**(M3). */
export function toServerDoc(s: LocalSnapshot): DashboardLayoutDoc {
  return {
    schemaVersion: LAYOUT_SCHEMA_VERSION,
    activeBoardId: s.activeBoardId,
    boards: stripCorp(s.boards),
    seenForced: s.seenForced,
    hiddenBoardIds: s.hiddenBoardIds,
    defaultBoardId: s.defaultBoardId,
  };
}

/** `anon`(누구 것인지 모름 · M5)과 빈 사용자는 동기화하지 않는다. */
export function canSync(userId: string): boolean {
  return userId !== '' && userId !== 'anon';
}

export type HydrateDecision =
  /** 서버를 못 쓴다(404 · 501 · 실패) — 지금과 같이 로컬만. */
  | { do: 'local-only' }
  /** 서버엔 없고 로컬엔 있다 — 처음 올린다. */
  | { do: 'push-local'; importedFrom: 'local-v1' }
  /** 양쪽 다 없다 — 처음 여는 사람. 첫 변경 때 올린다. */
  | { do: 'first-visit' }
  /** 표식의 판과 서버 판이 같다 — 아무것도 안 한다. */
  | { do: 'same'; revision: number }
  /** 서버가 더 최신이다(다른 기기에서 고침) — 서버로 교체. */
  | { do: 'take-server'; revision: number }
  /**
   * 서버에 있는데 이 브라우저는 맞춰 본 적이 없고 로컬에도 배치가 있다.
   * Q-2 결정: **서버 우선** · 로컬 본문은 `before-sync` 로 보관 · 안내 **한 번**.
   */
  | { do: 'take-server-keep-local-copy'; revision: number };

/**
 * hydrate 때 무엇을 할지 (web-plan §2.2 의 다섯 갈래 그대로).
 *
 * ★ 판정만 한다 — 실제 교체 · 올리기 · 표식 쓰기는 부르는 쪽이 한다.
 */
export function decideHydrate(args: {
  fetched: LayoutFetch;
  hasLocal: boolean;
  marker: SyncMarker | null;
}): HydrateDecision {
  const { fetched, hasLocal, marker } = args;

  if (fetched.kind !== 'ok') return { do: 'local-only' };

  // 서버에 문서가 없다
  if (fetched.layout === null) {
    return hasLocal ? { do: 'push-local', importedFrom: 'local-v1' } : { do: 'first-visit' };
  }

  // 서버에 문서가 있다
  if (marker && marker.revision === fetched.revision && !marker.dirty) {
    return { do: 'same', revision: fetched.revision };
  }
  if (marker) return { do: 'take-server', revision: fetched.revision };
  return hasLocal
    ? { do: 'take-server-keep-local-copy', revision: fetched.revision }
    : { do: 'take-server', revision: fetched.revision };
}
